import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RichEditor from "../design/RichEditor";
import RichView from "../design/RichView";
import ProgressChoice from "../design/ProgressChoice";
import { Banner, Disclosure, Progress } from "../mui";
import {
  MdAdd,
  MdCheck,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdErrorOutline,
  MdWarningAmber,
} from "react-icons/md";

/**
 * 工項詳情 — 參考區（照抄設計稿 el-pm-template_4.html:686-748）
 *
 * 結構是「一個『其他資料』標題 ＋ 一串收合區（Disclosure）」，
 * 全部預設收合。前五個依設計稿順序：做到什麼程度算完成、以前的紀錄、留言、
 * 誰在看這件事、代某某回報。
 *
 * 第六個「更多欄位」是設計稿沒有的：欄位格、解決方式、子項、相依、工時估vs實、附件、
 * 審核簽核這幾塊在新版面裡沒有位置，但**功能不准刪**，所以照硬規則 8 收進收合區，
 * 而不是刪掉。設計稿沒有位置 ≠ 這些資料可以消失。
 *
 * 空的區塊一律不畫（硬規則 2）：沒有的東西只在最後留一排「＋ 加入…」文字入口。
 */

/** 一行排開、可換行的按鈕／文字列 */
const inlineSx = { flexWrap: "wrap", alignItems: "center" };

/** 小節標題（收合區裡的 h3） */
function SectionTitle({ children }) {
  return (
    <Typography variant="h3" component="h3">
      {children}
    </Typography>
  );
}

SectionTitle.propTypes = { children: PropTypes.node.isRequired };

/** 次要說明小字 */
function Meta({ children, sx: sxProp }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={sxProp}>
      {children}
    </Typography>
  );
}

Meta.propTypes = { children: PropTypes.node.isRequired, sx: PropTypes.object };
Meta.defaultProps = { sx: undefined };

/** 勾選狀態的圖示（唯讀時放在文字前面） */
const checkIconStyle = { verticalAlign: "-0.125em" };

/**
 * 回報時附的檔案 —— 一顆「下載 檔名」的按鈕，底下一行大小（與誰、什麼時候附的）。
 *
 * 照個人工項（pages/MyTask/Index.js）附件區的寫法：外框鈕、整行寬、檔名可以換行、
 * 直向排（檔名一行、大小與誰附的一行），沒有空白的長英數也要能斷行。
 * 按鈕本身就寫「下載 {檔名}」，不另外放一顆只寫「下載」的鈕 —— 一頁好幾顆「下載」
 * 念給螢幕報讀器聽時分不出是哪一個檔案。
 */
function FileButton({ file, detail, onDownload }) {
  const meta = [file.fileSizeText, detail].filter(Boolean).join(" · ");
  return (
    <Button
      variant="outlined"
      fullWidth
      onClick={() => onDownload(file)}
      sx={{
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.5,
        py: 1.5,
        textAlign: "start",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        borderRadius: "var(--radius-sm)",
      }}
    >
      <span>下載 {file.fileName || "附件"}</span>
      {meta && (
        <Typography variant="body2" color="text.secondary" component="span">
          {meta}
        </Typography>
      )}
    </Button>
  );
}

FileButton.propTypes = {
  file: PropTypes.shape({ fileName: PropTypes.string, fileSizeText: PropTypes.string }).isRequired,
  detail: PropTypes.string,
  onDownload: PropTypes.func.isRequired,
};
FileButton.defaultProps = { detail: "" };

/** 審核那一段最多直接列幾個檔案；再多就講明其餘的在「以前的紀錄」裡 */
const REVIEW_FILES_MAX = 3;

/**
 * 審核中、而且回報有附檔案時，在「審核」那一段直接列出來（最新的在最上面）。
 *
 * 為什麼要在這裡再列一次：審核者要看過交付物才能決定「通過」或「要求修改」，
 * 可是檔案只掛在「以前的紀錄」裡某一筆回報底下，那一區預設收合、可能有十幾筆。
 * 只列最近幾個，其餘的講清楚還有幾個、在哪裡 —— 不要讓人以為就只有這幾個。
 *
 * 標題寫「回報進度時附的檔案」而不是「送審附的檔案」：這些檔案不一定是送審那一刻附的
 * （也可能是前幾次回報就放上去的），寫成送審時附的是替系統說了它不知道的事。
 */
function ReviewFiles({ files, attachmentCount, forDecision, onDownload }) {
  const shown = files.slice(0, REVIEW_FILES_MAX);
  const rest = files.length - shown.length;
  return (
    <Stack spacing={1}>
      {files.length > 0 && (
        <Typography component="h4" sx={{ fontWeight: 600 }}>
          回報進度時附加的檔案（{files.length} 個）
        </Typography>
      )}
      {(forDecision || files.length > 1) && (
        <Meta>
          {forDecision ? "按「通過」或「要求修改」前，請先下載檢視。" : ""}
          {files.length > 1 ? "依時間由新至舊排列。" : ""}
        </Meta>
      )}
      {/*
        「我的回報」卡上「送出回報」旁邊那顆「＋ 加入附件」放的是工項附件，不會進這份清單。
        負責人常用那顆附交付物，所以這裡一定要講：清單不是全部，工項附件也要一起看。
      */}
      {attachmentCount > 0 && (
        <Meta>
          {files.length > 0
            ? `此處僅列出回報進度時附加的檔案；另有 ${attachmentCount} 個工項附件，請一併檢視上方「工項附件」。`
            : `交付檔案可能位於工項附件：上方「工項附件」共 ${attachmentCount} 個檔案。`}
        </Meta>
      )}
      {shown.map((f) => (
        <FileButton
          key={`rf-${f.recordId}`}
          file={f}
          detail={[f.actor, f.time].filter(Boolean).join(" · ")}
          onDownload={onDownload}
        />
      ))}
      {rest > 0 && (
        <Meta>另有 {rest} 個較早附加的檔案，請至上方「活動紀錄」中對應的回報下載。</Meta>
      )}
    </Stack>
  );
}

ReviewFiles.propTypes = {
  files: PropTypes.array.isRequired,
  attachmentCount: PropTypes.number,
  forDecision: PropTypes.bool,
  onDownload: PropTypes.func.isRequired,
};
ReviewFiles.defaultProps = { forDecision: false, attachmentCount: 0 };

/** 「輸入一行 ＋ 加進去」：Enter 也會送出 */
function AddLine({ id, label, help, placeholder, value, onChange, onAdd, busy }) {
  return (
    <Stack spacing={1.5}>
      <TextField
        id={id}
        label={label}
        helperText={help || undefined}
        value={value}
        placeholder={placeholder || undefined}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onAdd();
          }
        }}
        fullWidth
      />
      <div>
        <Button variant="outlined" disabled={busy} loading={busy} loadingPosition="start" onClick={onAdd}>
          新增
        </Button>
      </div>
    </Stack>
  );
}

AddLine.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  help: PropTypes.node,
  placeholder: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};
AddLine.defaultProps = { help: null, placeholder: "", busy: false };
function TaskReference({
  readOnly,
  busy,
  criteria,
  onToggleDod,
  onRemoveDod,
  dodDraft,
  onChangeDodDraft,
  onAddDod,
  logs,
  comments,
  commentDraft,
  onChangeCommentDraft,
  onAddComment,
  onRemoveComment,
  watcherNames,
  watching,
  onToggleWatch,
  canProxy,
  assigneeName,
  proxyPct,
  currentPct,
  onChangeProxyPct,
  onProxyReport,
  facts,
  solHtml,
  onSaveSol,
  subs,
  subDraft,
  onChangeSubDraft,
  onAddSub,
  onToggleSub,
  deps,
  onOpenTask,
  workload,
  attachments,
  onDownloadAttachment,
  onRemoveAttachment,
  progressFiles,
  progressUnavailable,
  onDownloadProgressFile,
  review,
  devLinks,
  tests,
  onAddAttachment,
  onLinkCode,
  code,
  updatedText,
}) {
  /* 這三個都是「本來沒有東西，按了＋才出現的輸入格」，純畫面狀態 */
  const [dodEntry, setDodEntry] = useState(false);
  const [subEntry, setSubEntry] = useState(false);
  const [solEditing, setSolEditing] = useState(false);
  /*
   * 「更多欄位」是開還是合，只為了 data-tour 要不要掛上去。
   * GuidedTour 對「找不到選擇器」有好好的退路（整頁淡出＋置中說明），
   * 但對「找得到、卻是收合狀態」沒有 —— getBoundingClientRect 會回 0×0，
   * 導覽框就縮成左上角一個點。收合時乾脆不掛屬性，讓它走那條退路。
   */
  const [moreOpen, setMoreOpen] = useState(false);
  /* 其餘收合區也做成受控的，標頭右側的「展開／收合」字樣才知道現在該寫哪一個 */
  const [fold, setFold] = useState({});
  const foldProps = (k) => ({
    open: !!fold[k],
    onToggle: (next) => setFold((s) => ({ ...s, [k]: next })),
    summaryExtra: fold[k] ? "收合" : "展開",
  });
  /* RichEditor 是非受控的，編輯中的 HTML 只能先接在 ref 上，存檔時再送出去 */
  const solDraftRef = useRef(solHtml);

  const dodDone = criteria.filter((c) => c.is_checked).length;
  const hasSol = !!String(solHtml || "")
    .replace(/<[^>]*>/g, "")
    .trim() || /<img|<table/i.test(solHtml || "");

  /* 更多欄位：有內容的才算一項，全部都沒有時整塊不畫 */
  const moreBlocks = [
    facts.length > 0,
    hasSol || !readOnly,
    subs.length > 0,
    deps.blockedBy.length > 0 || deps.blocks.length > 0,
    !!workload,
    attachments.length > 0,
    !!review,
    devLinks.length > 0,
    tests.length > 0,
  ].filter(Boolean).length;

  const showComments = comments.length > 0 || !readOnly;
  /*
   * 「誰在看這件事」照設計稿畫：有人在看就列名單，沒有人在看時，
   * 還改得動的人仍要看得到那顆「＋ 我也要看」，否則永遠沒有第一個關注者。
   *
   * 後端是通的：NotificationController.cs:97 有 GET /Notification/Watch
   * （WatchStatus → NotificationService.GetWatchStatus，回 { watching, watchers }），
   * 第 109 行有 POST /Notification/Watch（SetWatch），資料存在 Model 的 Watch entity。
   * 名單也不會只靠手動加 —— 被指派、開單、留言、被 @ 提及都會由 AutoWatch 自動補進去。
   */
  const showWatchers = watcherNames.length > 0 || !readOnly;

  /*
   * 審核中（正在等把關者看）而且回報有附檔案，才在「審核」那一段列檔案；
   * 沒有檔案整塊不畫（硬規則 2）。審核的「更多欄位」預設是收合的，
   * 所以收合狀態的筆數那裡也要講一聲裡面有檔案，不然一樣沒人找得到。
   */
  const reviewFiles = review && review.status === "pending" ? progressFiles : [];
  /** 審核中時一併提醒工項附件：交付物可能是用「我的回報」卡的「＋ 加入附件」放上去的 */
  const reviewAttachmentCount = review && review.status === "pending" ? attachments.length : 0;
  const logFileCount = logs.filter((l) => l.hasFile).length;
  /** 回報內容裡貼的截圖總張數（跟上面的「附了檔案」是兩回事，不要相加） */
  const logShotCount = logs.reduce((n, l) => n + (Number(l.shotsCount) || 0), 0);

  /* 末尾的「＋ 加入…」只留真的還沒有的東西（設計稿 L744-748） */
  const entries = [];
  if (!readOnly && attachments.length === 0) entries.push("att");
  if (!readOnly && subs.length === 0) entries.push("sub");
  if (!readOnly && devLinks.length === 0) entries.push("dev");
  if (!readOnly && criteria.length === 0) entries.push("dod");

  return (
    <Box component="section" sx={{ mt: "var(--space-6)" }}>
      <Typography variant="h3" component="h2" sx={{ mb: 1.5 }}>
        其他資料
      </Typography>

      <Stack spacing={1.5}>
        {/* ① 做到什麼程度算完成（DoD） */}
        {criteria.length > 0 && (
          <Disclosure
            {...foldProps("dod")}
            data-tour="det-dod"
            title="驗收條件"
            count={`（${criteria.length} 項，已完成 ${dodDone} 項）`}
          >
            <Stack spacing={1}>
              {criteria.map((c) =>
                readOnly ? (
                  <Typography key={c.id}>
                    {c.is_checked ? <MdCheckBox aria-hidden="true" style={checkIconStyle} /> : <MdCheckBoxOutlineBlank aria-hidden="true" style={checkIconStyle} />} {c.content}
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={1.5} useFlexGap sx={inlineSx} key={c.id}>
                    {/* 勾選框改成有字的按鈕：只有一個小方框不算「一定有文字」（硬規則 3） */}
                    <Button
                      variant="text"
                      disabled={busy}
                      startIcon={c.is_checked ? <MdCheckBox aria-hidden="true" /> : <MdCheckBoxOutlineBlank aria-hidden="true" />}
                      onClick={() => onToggleDod(c)}
                    >
                      {c.is_checked ? "已完成" : "未完成"}
                    </Button>
                    {/* 中間這一段吃掉剩餘寬度，刪掉鈕才會靠右 */}
                    <Typography component="span" sx={{ flex: "1 1 auto", minWidth: 0 }}>
                      {c.content}
                    </Typography>
                    <Button variant="text" disabled={busy} onClick={() => onRemoveDod(c)}>
                      刪除
                    </Button>
                  </Stack>
                )
              )}

              {!readOnly && (
                <AddLine
                  id="task-dod-new"
                  label="新增驗收條件"
                  placeholder="例如：已於 iPad mini 完成測試"
                  value={dodDraft}
                  onChange={onChangeDodDraft}
                  onAdd={onAddDod}
                  busy={busy}
                />
              )}
            </Stack>
          </Disclosure>
        )}

        {/*
          進度回報讀不到時要講出來：少了回報，下面只剩「建立、指派、開始做」這些骨架，
          看起來像從來沒人回報過、也沒有交付物，其實是沒有權限看。
        */}
        {progressUnavailable && (
          <Meta>無法讀取此工項的進度回報（含回報時附加的檔案），可能是您沒有檢視權限；下方紀錄不代表無人回報。</Meta>
        )}

        {/* ② 以前的紀錄（活動紀錄） */}
        {logs.length > 0 && (
          /*
            收合時也要看得出「裡面有檔案可以下載、有截圖可以看」——
            這次的誤會就是從看不到開始的，而這塊預設是收合的。
          */
          <Disclosure
            {...foldProps("log")}
            data-tour="det-log"
            title="活動紀錄"
            count={`（${logs.length} 筆${logFileCount > 0 ? `，${logFileCount} 筆附有檔案` : ""}${
              logShotCount > 0 ? `，${logShotCount} 張截圖` : ""
            }）`}
          >
            <Stack spacing={1}>
              {logs.map((l, i) => (
                <div key={`${l.type}-${l.time}-${i}`}>
                  <Typography>
                    <strong>{l.actor}</strong> {l.text}
                    {l.backfill ? "（事後補登）" : ""}
                  </Typography>
                  {l.time && <Meta>{l.time}</Meta>}
                  {/*
                    回報**內容裡**貼的截圖。以前這一列只有純文字，截圖被剝成「[圖片×1]」——
                    審核者要看那張圖才判斷得了通過或要求修改，等於看不到交付物。

                    這裡刻意只放縮圖而不是整段 RichView：這是「一列一件事」的時間軸，
                    一張原尺寸截圖就會把整條軸撐開。縮圖高度固定（72px，超過 44px 的可點下限），
                    點下去走 RichView 既有的放大（Esc 關、放大時鎖底層捲動）。
                    上面那行字先說明「這裡有幾張圖、點了會放大」——
                    圖本身沒有文字，可點物件要有文字說明的規則由這一行負責。
                  */}
                  {(l.shotsCount > 0 || l.shotsBlocked > 0) && (
                    <Box sx={{ mt: 1 }}>
                      {/*
                        這句話要跟底下真的畫得出來的東西對得上：
                        網址過不了白名單的圖，RichView 會換成一段點不動的「圖片不能顯示」，
                        把它算進「點圖看原尺寸」等於叫人去點一個點不動的東西。
                        所以張數只算畫得出來的，擋掉的另外講一句。
                      */}
                      {l.shotsCount > 0 && <Meta>回報內含 {l.shotsCount} 張圖片，點選圖片可檢視原尺寸</Meta>}
                      {l.shotsBlocked > 0 && (
                        <Meta>
                          另有 {l.shotsBlocked} 張圖片無法顯示（網址並非可信任的圖片來源），
                          如需檢視請洽回報者。
                        </Meta>
                      )}
                      <RichView html={l.shotsHtml} className="rt-thumbs" />
                    </Box>
                  )}
                  {/* 回報時夾的檔案。沒有檔案的列不多任何東西，長相跟以前一樣 */}
                  {l.hasFile && (
                    <Box sx={{ mt: 1 }}>
                      <FileButton file={l} onDownload={onDownloadProgressFile} />
                    </Box>
                  )}
                </div>
              ))}
            </Stack>
          </Disclosure>
        )}

        {/* ③ 留言 */}
        {showComments && (
          <Disclosure {...foldProps("cmt")} title="留言" count={comments.length > 0 ? `（${comments.length} 則）` : "（尚無留言）"}>
            <Stack spacing={2}>
              {comments.map((cm) => (
                <div key={cm.id}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {cm.actor}{" "}
                    <Typography variant="body2" color="text.secondary" component="span">
                      {cm.time}
                    </Typography>
                  </Typography>
                  {/*
                    內容是後端原樣存下來的純文字；這裡的 html 是 design/domain.js 的
                    mentionHtml 產物，它把字跳脫後只替「@某某」上個顏色，是上色不是連結。

                    真正通知人的是後端：TaskService.PostTaskComment 存完留言後呼叫
                    NotificationService.HandleCommentMentions，用 @([A-Za-z0-9_.-]+)
                    抓出帳號、比對 admin.account，對得上的人才會拿到 mentioned 通知
                    （並自動變成這件事的關注者）。
                    mentionHtml 連「@中文名」也一起上色，但那種寫法後端抓不到 ——
                    所以下面那句說明寫的是「帳號」，不是「名字」。
                  */}
                  <Typography dangerouslySetInnerHTML={{ __html: cm.html }} />
                  {!readOnly && cm.id != null && (
                    <Button variant="text" disabled={busy} onClick={() => onRemoveComment(cm)}>
                      刪除留言
                    </Button>
                  )}
                </div>
              ))}

              {!readOnly && (
                <>
                  {/*
                    說明那句話是有憑據的（理由見上面那段）：@ 上對方的帳號，他真的會收到
                    一則指名給他的通知，而且會自動開始關注這件事。
                    寫「帳號」不寫「名字」是因為後端只比對 admin.account；
                    打中文名字只會變色，通知不到人。
                  */}
                  <TextField
                    placeholder="輸入留言，可輸入 @ 提及同事"
                    id="task-comment"
                    label="新增留言"
                    helperText="輸入 @ 加上對方的登入帳號（英文帳號），對方將收到通知，並自動追蹤此工項的進度。"
                    multiline
                    minRows={2}
                    value={commentDraft}
                    onChange={(e) => onChangeCommentDraft(e.target.value)}
                    fullWidth
                  />
                  <div>
                    <Button variant="outlined" disabled={busy} loading={busy} loadingPosition="start" onClick={onAddComment}>
                      送出留言
                    </Button>
                  </div>
                </>
              )}
            </Stack>
          </Disclosure>
        )}

        {/* ④ 誰在看這件事 */}
        {showWatchers && (
          /* 還沒有人在看時寫「還沒有人在看」而不是「（0 人）」—— 收合狀態下也要看得懂 */
          <Disclosure
            {...foldProps("watch")}
            title="追蹤者"
            count={watcherNames.length > 0 ? `（${watcherNames.length} 人）` : "（尚無追蹤者）"}
          >
            {watcherNames.length > 0 ? (
              <>
                <Typography>{watcherNames.join(" · ")}</Typography>
                <Meta sx={{ mt: 1 }}>此工項有進度更新時，以上人員皆會收到通知。</Meta>
              </>
            ) : (
              <Typography>尚無追蹤者。</Typography>
            )}
            {!readOnly && (
              <Box sx={{ mt: 2 }}>
                {/*
                  設計稿寫的是「＋ 加人進來看」，這裡維持「＋ 我也要看」：
                  POST /Notification/Watch 的 ReqSetWatch 只有 EntityType / EntityId / Watch
                  三個欄位，NotificationService.SetWatch 訂閱的對象固定是 GetAccount()
                  ——也就是按下去的人自己，沒有任何端點可以替別人訂閱。
                  要把別人拉進來看，現在的做法是在留言裡 @ 他：
                  HandleCommentMentions 會替被提及的人 AutoWatch（source = auto_mention）。
                */}
                <Button
                  variant="text"
                  startIcon={watching ? undefined : <MdAdd aria-hidden="true" />}
                  onClick={onToggleWatch}
                >
                  {watching ? "取消追蹤" : "追蹤"}
                </Button>
              </Box>
            )}
          </Disclosure>
        )}

        {/* ⑤ 代負責人回報（只有專案經理看得到） */}
        {canProxy && !readOnly && (
          <Disclosure {...foldProps("proxy")} title={`代${assigneeName || "負責人"}回報`} count="（僅專案經理可見）">
            <Typography>
              {assigneeName || "負責人"}以口頭告知進度時，可使用此功能代為回報。系統將記錄為「代
              {assigneeName || "負責人"}回報」，不會列為您的工項。
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              {/* 與個人工項的回報卡用同一個元件（見 ProgressChoice）：
                  同一件事在同一套系統裡不該有兩種填法 */}
              <ProgressChoice
                id="task-proxy-pct"
                label={`${assigneeName || "負責人"}回報的進度`}
                help={`此工項目前進度為 ${currentPct}%。`}
                value={proxyPct}
                currentPct={currentPct}
                onChange={onChangeProxyPct}
                disabled={busy}
              />
              <div>
                <Button variant="outlined" disabled={busy} loading={busy} loadingPosition="start" onClick={onProxyReport}>
                  代{assigneeName || "負責人"}送出
                </Button>
              </div>
            </Stack>
          </Disclosure>
        )}

        {/* ⑥ 更多欄位 —— 設計稿沒有位置、但不准刪的那幾塊 */}
        {moreBlocks > 0 && (
          <Disclosure
            open={moreOpen}
            onToggle={setMoreOpen}
            summaryExtra={moreOpen ? "收合" : "展開"}
            title="更多欄位"
            count={`（${moreBlocks} 項${
              reviewFiles.length > 0 ? `，審核區含 ${reviewFiles.length} 個回報附加的檔案` : ""
            }）`}
          >
            <Stack spacing={4}>
              {/* 欄位格 */}
              {facts.length > 0 && (
                <Stack spacing={1}>
                  <SectionTitle>基本欄位</SectionTitle>
                  {facts.map((f) => (
                    <Typography key={f.label}>
                      {f.label}：{f.value}
                    </Typography>
                  ))}
                </Stack>
              )}

              {/* 解決方式 / 技術方案（設計稿沒有這一塊，但它是 RD 唯一寫做法的地方） */}
              {(hasSol || !readOnly) && (
                <Stack spacing={1} data-tour={moreOpen ? "det-sol" : undefined}>
                  <SectionTitle>解決方式</SectionTitle>
                  {solEditing ? (
                    <>
                      <RichEditor
                        id="task-sol-editor"
                        html={solHtml}
                        placeholder="說明做法、修改範圍與測試方式…"
                        onChange={(h) => {
                          solDraftRef.current = h;
                        }}
                      />
                      <Stack direction="row" spacing={1.5} useFlexGap sx={inlineSx}>
                        <Button
                          variant="outlined"
                          disabled={busy}
                          loading={busy}
                          loadingPosition="start"
                          onClick={async () => {
                            await onSaveSol(solDraftRef.current);
                            setSolEditing(false);
                          }}
                        >
                          儲存
                        </Button>
                        <Button variant="text" onClick={() => setSolEditing(false)}>
                          取消
                        </Button>
                      </Stack>
                    </>
                  ) : (
                    <>
                      {hasSol ? (
                        <RichView
                          html={solHtml}
                          sxStyle="font-size:var(--t-body);line-height:var(--t-body-lh);color:var(--ink);overflow-x:auto;"
                        />
                      ) : (
                        <Typography>尚無解決方式。</Typography>
                      )}
                      {!readOnly && onSaveSol && (
                        <div>
                          <Button
                            variant="text"
                            startIcon={hasSol ? undefined : <MdAdd aria-hidden="true" />}
                            onClick={() => {
                              solDraftRef.current = solHtml;
                              setSolEditing(true);
                            }}
                          >
                            {hasSol ? "編輯解決方式" : "填寫解決方式"}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </Stack>
              )}

              {/* 小項目（子任務） */}
              {subs.length > 0 && (
                <Stack spacing={1}>
                  <SectionTitle>
                    小項目（完成 {subs.filter((s) => s.done).length}／{subs.length}）
                  </SectionTitle>
                  {subs.map((c, i) =>
                    readOnly ? (
                      <Typography key={c.id || `${c.text}-${i}`}>
                        {c.done ? <MdCheckBox aria-hidden="true" style={checkIconStyle} /> : <MdCheckBoxOutlineBlank aria-hidden="true" style={checkIconStyle} />} {c.text}
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={1.5} useFlexGap sx={inlineSx} key={c.id || `${c.text}-${i}`}>
                        <Button
                          variant="text"
                          disabled={busy}
                          startIcon={c.done ? <MdCheckBox aria-hidden="true" /> : <MdCheckBoxOutlineBlank aria-hidden="true" />}
                          onClick={() => onToggleSub(i)}
                        >
                          {c.done ? "已完成" : "未完成"}
                        </Button>
                        <Typography component="span">{c.text}</Typography>
                      </Stack>
                    )
                  )}
                  {!readOnly && (
                    <AddLine
                      id="task-sub-new"
                      label="新增小項目"
                      value={subDraft}
                      onChange={onChangeSubDraft}
                      onAdd={onAddSub}
                      busy={busy}
                    />
                  )}
                </Stack>
              )}

              {/* 相依關係 */}
              {(deps.blockedBy.length > 0 || deps.blocks.length > 0) && (
                <Stack spacing={1}>
                  <SectionTitle>相依關係</SectionTitle>
                  {deps.blockedBy.length > 0 && (
                    <>
                      <Typography>此工項須待下列工項完成：</Typography>
                      <Stack direction="row" spacing={1.5} useFlexGap sx={inlineSx}>
                        {deps.blockedBy.map((d) => (
                          <Button variant="text" key={`b-${d.code}`} onClick={() => onOpenTask(d)}>
                            {d.code} {d.name}
                          </Button>
                        ))}
                      </Stack>
                    </>
                  )}
                  {deps.blocks.length > 0 && (
                    <>
                      <Typography>下列工項正等待此工項完成：</Typography>
                      <Stack direction="row" spacing={1.5} useFlexGap sx={inlineSx}>
                        {deps.blocks.map((d) => (
                          <Button variant="text" key={`k-${d.code}`} onClick={() => onOpenTask(d)}>
                            {d.code} {d.name}
                          </Button>
                        ))}
                      </Stack>
                    </>
                  )}
                </Stack>
              )}

              {/* 工時（估 vs 實） */}
              {workload && (
                <Stack spacing={1}>
                  <SectionTitle>工時（預估與實際）</SectionTitle>
                  <Typography>
                    預估 {workload.est} 小時，實際已投入 {workload.act} 小時
                    {workload.over ? "，已超出預估工時。" : "。"}
                  </Typography>
                  <Box role="img" aria-label={`工時 ${workload.pct}%`} sx={{ maxWidth: 420 }}>
                    <Progress value={workload.pct} tone={workload.over ? "attention" : "progress"} />
                  </Box>
                </Stack>
              )}

              {/*
                工項附件（/Task/Attachment）。標題寫明「工項」：回報進度時附的檔案
                是另一份（掛在回報上、刪除權限也不同），不合併成一份清單，
                但要讓人知道那些檔案不在這裡、在哪裡。
              */}
              {attachments.length > 0 && (
                <Stack spacing={1}>
                  <SectionTitle>工項附件（{attachments.length} 個）</SectionTitle>
                  {logFileCount > 0 && (
                    <Meta>回報進度時附加的 {logFileCount} 個檔案不在此處，請至上方「活動紀錄」下載。</Meta>
                  )}
                  {attachments.map((a) => (
                    <Stack direction="row" spacing={1.5} useFlexGap sx={inlineSx} key={a.id}>
                      {/* 中間這段要吃掉剩餘寬度，按鈕才會靠右 */}
                      <Typography component="span" sx={{ flex: "1 1 auto", minWidth: 0 }}>
                        {a.name}
                        {a.meta ? ` · ${a.meta}` : ""}
                      </Typography>
                      <Button variant="text" onClick={() => onDownloadAttachment(a.raw)}>
                        下載
                      </Button>
                      {!readOnly && (
                        <Button variant="text" onClick={() => onRemoveAttachment(a.raw)}>
                          刪除
                        </Button>
                      )}
                    </Stack>
                  ))}
                  {!readOnly && (
                    <div>
                      <Button variant="text" startIcon={<MdAdd aria-hidden="true" />} onClick={onAddAttachment}>
                        加入附件
                      </Button>
                    </div>
                  )}
                </Stack>
              )}

              {/* 審核簽核 */}
              {review && (
                <Stack spacing={1} data-tour={moreOpen ? "det-review" : undefined}>
                  <SectionTitle>審核</SectionTitle>

                  {/* 唯讀（審核抽屜）：只講這一次審核的來龍去脈，不給任何按鈕 */}
                  {readOnly ? (
                    <>
                      {review.active ? (
                        <>
                          <Typography>
                            {review.active.typeLabel}
                            {review.active.openerName ? ` · ${review.active.openerName} 送審` : ""}
                            {review.active.openedAt ? ` · ${review.active.openedAt}` : ""}
                            {review.active.statusLabel ? ` · ${review.active.statusLabel}` : ""}
                            {review.active.sla ? ` · 剩餘 ${review.active.sla}` : ""}
                          </Typography>
                          {review.active.summary && <Typography>送審說明：{review.active.summary}</Typography>}
                          {review.active.decisions.map((d, i) => (
                            <Typography key={`${d.name}-${i}`}>
                              {d.name}：{d.label}
                              {d.at ? ` · ${d.at}` : ""}
                              {d.comment ? ` · ${d.comment}` : ""}
                            </Typography>
                          ))}
                        </>
                      ) : (
                        <Typography>此工項尚未送審。</Typography>
                      )}
                      {/* 唯讀檢視沒有決定鈕，但下載不會改到工項，照樣給 */}
                      {(reviewFiles.length > 0 || reviewAttachmentCount > 0) && (
                        <ReviewFiles
                          files={reviewFiles}
                          attachmentCount={reviewAttachmentCount}
                          onDownload={onDownloadProgressFile}
                        />
                      )}
                      {review.rounds.length > 0 && (
                        <>
                          <Meta>其他輪次</Meta>
                          {review.rounds.map((r) => (
                            <Typography key={`rv-${r.id}`}>
                              {r.typeLabel} · {r.at} · {r.statusLabel}
                            </Typography>
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {review.status === "pending" && (
                        <Banner kind="warn" icon={<MdErrorOutline />}>
                          等待{review.bannerReviewers || "審核人"}審核（{review.latestTypeLabel}）。
                        </Banner>
                      )}
                      {/*
                        審核通過會自動完成，所以這裡不會再叫人「去按完成」。
                        唯一的例外是通過了卻還停在審核中（自動完成上線前核准的舊資料，
                        或自動轉換被驗收條件之類的守門擋下）—— 那時「⋯ 更多」裡會多一顆
                        「審核已通過，標記為已完成」（TaskDetailPanel 的 stuckDone），要講出來它在哪裡，
                        而且用 warn：那是一件還沒收尾的事，不是單純的通知。
                        其餘狀態（已完成、重新打開後的進行中）只講審核本身，不替工項多說什麼。
                      */}
                      {review.status === "approved" && review.taskStatus === "review" && (
                        <Banner kind="warn" icon={<MdErrorOutline />}>
                          審核已通過，但此工項仍停留在「審核中」，未自動轉為已完成。
                          請開啟上方「⋯ 更多」，點選「審核已通過，標記為已完成」；若無法執行，該處會說明受阻原因。
                        </Banner>
                      )}
                      {review.status === "approved" && review.taskStatus !== "review" && (
                        <Banner kind="info" icon={<MdCheck />}>
                          {review.taskStatus === "done"
                            ? "審核已通過，此工項已完成。"
                            : "審核已通過。"}
                        </Banner>
                      )}
                      {/* 退回的那一刻後端就把工項改回進行中；真的是進行中才這樣寫，舊資料照原本的說法 */}
                      {review.status === "rejected" && (
                        <Banner kind="attention" icon={<MdWarningAmber />}>
                          {review.taskStatus !== "progress"
                            ? "已退回要求修改，修改完成後請重新送審。"
                            : review.taskProgress >= 100
                            ? "已退回要求修改，狀態已回到「進行中」，但進度仍為 100%。回報時請填寫實際進度；送出 100% 將立即重新送審。"
                            : `已退回要求修改，狀態已回到「進行中」（進度 ${review.taskProgress}%）。修改完成後將進度更新為 100%，即會重新送審。`}
                        </Banner>
                      )}

                      {review.status === "none" && (
                        <>
                          <Typography>
                            {review.staleApproval
                              ? "上次審核通過後，此工項曾重新開啟，重做部分尚未經過審核。請選擇審核人後重新送審。"
                              : "尚未送審。請選擇審核人後送出。"}
                          </Typography>
                          <TextField
                            select
                            id="task-review-type"
                            label="審核類型"
                            value={review.typeValue}
                            onChange={(e) => review.onChangeType(e.target.value)}
                            slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                            sx={{ maxWidth: 420 }}
                          >
                            {review.typeOptions.map((o) => (
                              <option key={o}>{o}</option>
                            ))}
                          </TextField>
                          <Typography>
                            {review.selfIsManager
                              ? "審核人（您是此專案的專案經理，可審核自己的工項）"
                              : "審核人（不可選擇負責人本人，以確保經他人審核）"}
                          </Typography>
                          <Stack direction="row" spacing={1.5} useFlexGap sx={inlineSx}>
                            {review.reviewerChips.map((m) => (
                              <Button
                                variant={review.picked.includes(m.account) ? "outlined" : "text"}
                                key={m.account}
                                aria-pressed={review.picked.includes(m.account)}
                                startIcon={review.picked.includes(m.account) ? <MdCheck aria-hidden="true" /> : undefined}
                                onClick={() => review.onToggleReviewer(m.account)}
                              >
                                {m.name || m.account}
                              </Button>
                            ))}
                          </Stack>
                          <div>
                            <Button
                              variant="outlined"
                              disabled={busy || review.picked.length === 0}
                              loading={busy}
                              loadingPosition="start"
                              onClick={review.onStartReview}
                            >
                              送出給審核人
                            </Button>
                          </div>
                        </>
                      )}

                      {/* 放在「正在等 X 看」與「通過／要求修改」中間：先看交付物，再做決定 */}
                      {(reviewFiles.length > 0 || reviewAttachmentCount > 0) && (
                        <ReviewFiles
                          files={reviewFiles}
                          attachmentCount={reviewAttachmentCount}
                          forDecision={!!review.isPendingReviewer}
                          onDownload={onDownloadProgressFile}
                        />
                      )}

                      {review.status === "pending" && review.canDecide && (
                        <Stack direction="row" spacing={1.5} useFlexGap sx={inlineSx}>
                          <Button variant="outlined" disabled={busy} loading={busy} loadingPosition="start" onClick={() => review.onDecide(1)}>
                            通過
                          </Button>
                          <Button variant="outlined" color="error" disabled={busy} onClick={() => review.onDecide(2)}>
                            要求修改
                          </Button>
                        </Stack>
                      )}

                      {review.status === "rejected" && (
                        <div>
                          <Button variant="outlined" disabled={busy} loading={busy} loadingPosition="start" onClick={review.onResubmit}>
                            重新送審
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </Stack>
              )}

              {/* 關聯程式碼：GET /Task/DevLinks 讀得到，但沒有建立端點，所以有資料才畫 */}
              {devLinks.length > 0 && (
                <Stack spacing={1}>
                  <SectionTitle>關聯程式碼（{devLinks.length} 筆）</SectionTitle>
                  {devLinks.map((d) => (
                    <Typography key={d.key}>
                      {d.kind} {d.label} · {d.ciText}
                    </Typography>
                  ))}
                </Stack>
              )}

              {/* 關聯測試案例（後端 /Task/Tests 尚未完成，有資料才畫） */}
              {tests.length > 0 && (
                <Stack spacing={1}>
                  <SectionTitle>關聯測試案例（{tests.length} 筆）</SectionTitle>
                  {tests.map((t) => (
                    <Typography key={t.key}>
                      {t.name} · {t.result}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Stack>
          </Disclosure>
        )}
      </Stack>

      {/* 還沒有的東西：只留文字入口，不畫空區塊（設計稿 L744-748） */}
      {entries.length > 0 && (
        <>
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ ...inlineSx, mt: 2 }}>
            <Typography variant="body2" color="text.secondary" component="span">
              尚未新增：
            </Typography>
            {/*
              寫「工項附件」而不是「附件」：原本這句讓人以為整件事一個檔案都沒有，
              其實回報進度時附的檔案好好地掛在回報上 —— 審核者就是被這句話誤導的。
              兩種檔案不合併（來源與刪除權限都不同），只把名字講清楚、再補一句在哪裡。
            */}
            {entries.includes("att") && (
              <Button variant="text" startIcon={<MdAdd aria-hidden="true" />} onClick={onAddAttachment}>
                加入工項附件
              </Button>
            )}
            {entries.includes("sub") && (
              <Button
                variant="text"
                startIcon={<MdAdd aria-hidden="true" />}
                aria-expanded={subEntry}
                onClick={() => setSubEntry((s) => !s)}
              >
                加入小項目
              </Button>
            )}
            {entries.includes("dev") && (
              <Button variant="text" startIcon={<MdAdd aria-hidden="true" />} onClick={onLinkCode}>
                關聯程式碼
              </Button>
            )}
            {entries.includes("dod") && (
              <Button
                variant="text"
                startIcon={<MdAdd aria-hidden="true" />}
                aria-expanded={dodEntry}
                onClick={() => setDodEntry((s) => !s)}
              >
                新增驗收條件
              </Button>
            )}
          </Stack>

          {entries.includes("att") && logFileCount > 0 && (
            <Meta sx={{ mt: 1 }}>
              此工項尚無工項附件；回報進度時附加的 {logFileCount} 個檔案另列於上方「活動紀錄」，請至該處下載。
            </Meta>
          )}

          {subEntry && (
            <Box sx={{ mt: 2 }}>
              <AddLine
                id="task-sub-first"
                label="小項目內容"
                value={subDraft}
                onChange={onChangeSubDraft}
                onAdd={onAddSub}
                busy={busy}
              />
            </Box>
          )}

          {dodEntry && (
            <Box sx={{ mt: 2 }}>
              <AddLine
                id="task-dod-first"
                label="驗收條件"
                help="每條填寫一項條件，例如：鍵盤開啟時仍可點選結帳按鈕。"
                value={dodDraft}
                onChange={onChangeDodDraft}
                onAdd={onAddDod}
                busy={busy}
              />
            </Box>
          )}
        </>
      )}

      <Meta sx={{ mt: "var(--space-6)" }}>
        <Typography
          component="code"
          sx={{ fontFamily: "var(--font-mono)", fontSize: "var(--t-code)", color: "text.secondary" }}
        >
          {code}
        </Typography>
        {updatedText ? ` · ${updatedText}更新` : ""}
      </Meta>
    </Box>
  );
}

TaskReference.propTypes = {
  readOnly: PropTypes.bool,
  busy: PropTypes.bool,
  criteria: PropTypes.array,
  onToggleDod: PropTypes.func,
  onRemoveDod: PropTypes.func,
  dodDraft: PropTypes.string,
  onChangeDodDraft: PropTypes.func,
  onAddDod: PropTypes.func,
  logs: PropTypes.array,
  /** [{ id, actor, time, html }]，html 已由 mentionHtml 處理過 */
  comments: PropTypes.array,
  commentDraft: PropTypes.string,
  onChangeCommentDraft: PropTypes.func,
  onAddComment: PropTypes.func,
  onRemoveComment: PropTypes.func,
  watcherNames: PropTypes.array,
  watching: PropTypes.bool,
  onToggleWatch: PropTypes.func,
  /** 代回報：由 hasPermission 決定，沒有權限就整塊不渲染 */
  canProxy: PropTypes.bool,
  assigneeName: PropTypes.string,
  proxyPct: PropTypes.string,
  /** 這件工項目前的進度（代回報的輸入是五顆大按鈕，空值要顯示現在是幾 %） */
  currentPct: PropTypes.number,
  onChangeProxyPct: PropTypes.func,
  onProxyReport: PropTypes.func,
  /** [{ label, value }]，只放真的有值的欄位 */
  facts: PropTypes.array,
  solHtml: PropTypes.string,
  onSaveSol: PropTypes.func,
  subs: PropTypes.array,
  subDraft: PropTypes.string,
  onChangeSubDraft: PropTypes.func,
  onAddSub: PropTypes.func,
  onToggleSub: PropTypes.func,
  deps: PropTypes.shape({ blockedBy: PropTypes.array, blocks: PropTypes.array }),
  onOpenTask: PropTypes.func,
  workload: PropTypes.shape({
    est: PropTypes.number,
    act: PropTypes.number,
    pct: PropTypes.number,
    over: PropTypes.bool,
  }),
  /** [{ id, name, meta, raw }] */
  attachments: PropTypes.array,
  onDownloadAttachment: PropTypes.func,
  onRemoveAttachment: PropTypes.func,
  /**
   * 回報進度時附的檔案（不是工項附件），最新的在前面：
   * [{ recordId, fileName, fileSize, fileSizeText, actor, time }]。
   * logs 裡有檔案的那幾筆也帶同樣的 recordId / fileName / fileSizeText / hasFile。
   */
  progressFiles: PropTypes.array,
  /** 進度回報讀不到（例如沒有權限）；true 時要講清楚，不能讓人以為沒有人回報過 */
  progressUnavailable: PropTypes.bool,
  /** 收到上面那種物件（或 logs 的一筆），用 recordId 下載 */
  onDownloadProgressFile: PropTypes.func,
  review: PropTypes.object,
  devLinks: PropTypes.array,
  tests: PropTypes.array,
  onAddAttachment: PropTypes.func,
  onLinkCode: PropTypes.func,
  code: PropTypes.string,
  updatedText: PropTypes.string,
};

TaskReference.defaultProps = {
  readOnly: false,
  busy: false,
  criteria: [],
  onToggleDod: () => {},
  onRemoveDod: () => {},
  dodDraft: "",
  onChangeDodDraft: () => {},
  onAddDod: () => {},
  logs: [],
  comments: [],
  commentDraft: "",
  onChangeCommentDraft: () => {},
  onAddComment: () => {},
  onRemoveComment: () => {},
  watcherNames: [],
  watching: false,
  onToggleWatch: () => {},
  canProxy: false,
  assigneeName: "",
  proxyPct: "",
  currentPct: 0,
  onChangeProxyPct: () => {},
  onProxyReport: () => {},
  facts: [],
  solHtml: "",
  onSaveSol: null,
  subs: [],
  subDraft: "",
  onChangeSubDraft: () => {},
  onAddSub: () => {},
  onToggleSub: () => {},
  deps: { blockedBy: [], blocks: [] },
  onOpenTask: () => {},
  workload: null,
  attachments: [],
  onDownloadAttachment: () => {},
  onRemoveAttachment: () => {},
  progressFiles: [],
  progressUnavailable: false,
  onDownloadProgressFile: () => {},
  review: null,
  devLinks: [],
  tests: [],
  onAddAttachment: () => {},
  onLinkCode: () => {},
  code: "",
  updatedText: "",
};

export default TaskReference;
